import { Either } from 'monet';

/**
 * @description Сотрудник рабочего расписания.
 * @param {string}  fullName
 * @param {string}  id
 * @param {boolean} isIntegrated
 */
function Employee(fullName, id, isIntegrated) {
  /**
   * @description Получить полное имя.
   * @returns {string}
   */
  this.getFullName = () => fullName;

  /**
   * @description Получить идентификатор.
   * @returns {string}
   */
  this.getId = () => id;

  /**
   * @description Получить интегрирован ли.
   * @returns {boolean}
   */
  this.getIsIntegrated = () => isIntegrated;
}

/**
 * @description Шлюз интеграций с агрегаторами.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function AggregatorIntegrationGateway(axios) {
  /**
   * @description Создать интеграцию с 32top.
   * @returns {Promise<Either<{title}, {key,title}>>}
   */
  this.create32top = (params) => axios
    .post('/api/aggregator-integrations/32top', params)
    .then(
      async (response) => Either.right({
        key: response.data.key,
        title: response.data.title,
      }),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * Изменить интеграцию с 32top.
   * @param params
   * @return {Promise<Either<unknown, {title}>>}
   */
  this.edit32top = (params) => axios
    .put('/api/aggregator-integrations/32top', params)
    .then(
      async (response) => Either.right({
        title: response.data.title,
      }),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Отключить сотрудника.
   * @param {string} id         Идентификатор интеграции.
   * @param {number} employeeId Идентификатор сотрудника.
   * @returns {Promise<Either<{title},{title}>>}
   */
  this.disableEmployee = (id, employeeId) => axios
    .delete(`/api/aggregator-integrations/${id}/employees/${employeeId}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Включить сотрудника.
   * @param {string} id         Идентификатор интеграции.
   * @param {number} employeeId Идентификатор сотрудника.
   * @returns {Promise<Either<{title},{title}>>}
   */
  this.enableEmployee = (id, employeeId) => axios
    .post(`/api/aggregator-integrations/${id}/employees/${employeeId}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить данные по интеграции с 32top.
   * @returns {Promise<Either<{title}, any>>}
   */
  this.get32top = () => {
    const makeEmployee = (item) => new Employee(
      item.full_name,
      item.id,
      item.is_integrated,
    );
    return axios
      .get('/api/aggregator-integrations/32top')
      .then(
        async (response) => Either.right({
          employees: (response.data.employees || []).map(makeEmployee),
          exists: response.data.exists,
          isPrintQrCode: response.data.is_print_qr_code,
          key: response.data.key,
          link: response.data.link,
          name: response.data.name,
        }),
        async (error) => {
          if (error.isAxiosError && error.response.status === 400) {
            return Either.left(error.response.data);
          }
          throw error;
        },
      );
  };

  /**
   * @description Создать интеграцию с Prodoctorov.
   * @param {any} request
   * @returns {Promise<Either<{title}, {message}>>}
   */
  this.createProdoctorov = (request) => axios
    .post('/api/aggregator-integrations/prodoctorov', request)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Изменить интеграцию с Prodoctorov.
   * @param request
   * @return {Promise<Either<{title}, {message}>>}
   */
  this.editProdoctorov = (request) => axios
    .put('/api/aggregator-integrations/prodoctorov', request)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить данные по интеграции с Prodoctorov.
   * @returns {Promise<Either<{title}, any>>}
   */
  this.getProdoctorov = () => axios
    .get('/api/aggregator-integrations/prodoctorov')
    .then(
      (response) => Either.right({
        exists: response.data.exists,
        name: response.data.name,
        token: response.data.token,
      }),
      (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
}
